/**
 * Sidebar width + collapsed state, persisted across sessions.
 *
 * Pointer events (not mousedown/mousemove) so the drag works with a mouse, a
 * pen and a touch contact from one code path, and pointer capture keeps the
 * drag alive when the cursor outruns the 6px handle.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { STORAGE_KEYS } from '../lib/constants';

export const SIDEBAR_MIN_WIDTH = 200;
export const SIDEBAR_MAX_WIDTH = 420;
/** Expanded / flyout — tighter than the old 248px labeled rail. */
export const SIDEBAR_DEFAULT_WIDTH = 212;
/** Icon-only rail (~64–72px): tooltips/aria carry labels. */
export const SIDEBAR_COLLAPSED_WIDTH = 68;

function clampWidth(value: number): number {
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, value));
}

function readStored<T>(key: string, fallback: T, parse: (raw: string) => T): T {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    return raw === null || raw === undefined ? fallback : parse(raw);
  } catch {
    // Private-mode / disabled storage must not break the shell.
    return fallback;
  }
}

function write(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* non-fatal */
  }
}

export function useSidebarLayout(): {
  width: number;
  isCollapsed: boolean;
  isDragging: boolean;
  toggleCollapsed: () => void;
  onHandlePointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onHandleKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
} {
  const [width, setWidth] = useState(() =>
    readStored(STORAGE_KEYS.sidebarWidth, SIDEBAR_DEFAULT_WIDTH, (raw) => {
      const parsed = Number.parseInt(raw, 10);
      return Number.isFinite(parsed) ? clampWidth(parsed) : SIDEBAR_DEFAULT_WIDTH;
    })
  );
  const [isCollapsed, setIsCollapsed] = useState(() =>
    readStored(STORAGE_KEYS.sidebarCollapsed, false, (raw) => raw === 'true')
  );
  const [isDragging, setIsDragging] = useState(false);
  const frame = useRef<number | null>(null);

  useEffect(() => write(STORAGE_KEYS.sidebarWidth, String(width)), [width]);
  useEffect(() => write(STORAGE_KEYS.sidebarCollapsed, String(isCollapsed)), [isCollapsed]);
  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    []
  );

  const toggleCollapsed = useCallback(() => setIsCollapsed((value) => !value), []);

  const onHandlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0 && event.pointerType === 'mouse') return;
      event.preventDefault();
      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);
      setIsDragging(true);

      const startX = event.clientX;
      const startWidth = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : width;

      const onMove = (move: PointerEvent): void => {
        // Coalesce to one update per frame: pointermove fires far faster than
        // paint, and setState per event makes the drag feel like it lags.
        if (frame.current !== null) cancelAnimationFrame(frame.current);
        frame.current = requestAnimationFrame(() => {
          const next = startWidth + (move.clientX - startX);
          // Dragging below the minimum collapses instead of fighting the clamp.
          if (next < SIDEBAR_MIN_WIDTH - 28) {
            setIsCollapsed(true);
          } else {
            setIsCollapsed(false);
            setWidth(clampWidth(next));
          }
        });
      };

      const onUp = (): void => {
        setIsDragging(false);
        handle.releasePointerCapture?.(event.pointerId);
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onUp);
        handle.removeEventListener('pointercancel', onUp);
      };

      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', onUp);
      handle.addEventListener('pointercancel', onUp);
    },
    [isCollapsed, width]
  );

  // A drag handle that only responds to a pointer is unusable by keyboard, so
  // arrows resize and Enter toggles (WAI-ARIA separator pattern).
  const onHandleKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    const STEP = 16;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setIsCollapsed(false);
      setWidth((current) => clampWidth(current - STEP));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setIsCollapsed(false);
      setWidth((current) => clampWidth(current + STEP));
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsCollapsed((value) => !value);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setIsCollapsed(false);
      setWidth(SIDEBAR_DEFAULT_WIDTH);
    }
  }, []);

  return { width, isCollapsed, isDragging, toggleCollapsed, onHandlePointerDown, onHandleKeyDown };
}
